import {
  Alert,
  App,
  Button,
  Checkbox,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Tag,
  Tooltip,
} from 'antd';
import { Plus, Trash2, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type {
  AssociatedUnitDto,
  CommandAskDto,
  CommandAskInputDto,
  CommandEntryInputDto,
  CommandRoleDto,
  Slot,
  Weekday,
} from '@pams/shared';
import {
  ARRANGEMENT_LIMITS,
  MAX_DUTY_DAYS,
  WEEKDAYS,
  WEEKDAY_LABELS,
  commandAddresseeCode,
  commandKeyOf,
  expandRankSelection,
  displaySlotLabel,
} from '@pams/shared';
import { DateTime } from 'luxon';
import { AddresseeCodes } from '@/components/AddresseeCodes';
import { AssociatedUnitsPicker } from '@/components/AssociatedUnitsPicker';
import { DelegationRowsEditor, type DelegationRow } from '@/components/fill/DelegationRowsEditor';
import { useRankOptions } from '@/hooks/useRankOptions';
import { DatePicker } from '@/components/DatePicker';
import { TimeField } from './TimeField';
import { SourceCell } from './SourceCell';
import { askRowsOf, askTargetKey, askTargetOf, askedAddressees, commandAskIsReal, expandAskRows } from './commandAsks';
import { notifyDevChange } from '@/lib/devNotice';
import { istNow } from '@/lib/istNow';
import { EmptyState } from '@/components/EmptyState';
import { OfficerPicker } from '@/components/OfficerPicker';
import { ReorderableList } from '@/components/ReorderableList';
import { pruneHiddenState } from '@/lib/arrangementShape';
import { nameOr } from '@/lib/selectOptions';
import { SURFACES } from '@/lib/surfaces';
import { cn } from '@/lib/utils';

/**
 * ⚠️ [C3a §6] ONE SUBSTITUTION FOR BOTH SLOT-LABEL READERS IN THIS FILE.
 *
 * `generateSlots` stamps `<date> · No fixed time` into `deployments.slot_label`, and §6 replaces
 * that phrase with the card's reporting time WHERE THE LABEL IS READ — never by rewriting the
 * stored value, which is `slotLabel.constant.ts`'s stated rule and is why there is no migration.
 *
 * ⚠️ [amended C5 §4a] IT DELEGATES TO `displaySlotLabel` NOW, AND THE `humaniseTimes` ORDERING
 * WENT WITH IT. That trap was stated here and at three other call sites; it is a property of
 * the shared function now. What this wrapper still owns is the SHAPE the builder's draft holds
 * its times in, which is why it survives rather than being inlined.
 *
 * ⚠️ AND A WHOLE-DAY CHIP WITH NO REPORTING TIME NOW READS `Whole day` RATHER THAN
 * `No fixed time` — the same words the officer and the printed sheet see for the same duty.
 *
 * ⚠️ `hasFixedTime: false` IS CORRECT FOR EVERY SLOT THIS RENDERS, INCLUDING A SHIFT ONE. The
 * substitution is a `String.replace` of a marker that only a whole-day label carries, so it is
 * a no-op on a shift chip — and the draft has no per-slot `hasFixedTime` to pass.
 *
 * ⚠️ A BLANK STRING IS NOT A TIME. `TimeField` buffers, so an emptied field is `''` rather than
 * `null`, and `toTwelveHour('')` would put an empty `From ` on every chip — which is why
 * `displaySlotLabel` leans on `wholeDayTimes`' own `presentTime` guard.
 */
const slotLabelWithTimes = (
  label: string,
  reportingTime: string | null | undefined,
  /* [STAND-DOWN] The optional end, so a whole-day chip reads `3:00 PM–9:00 PM`. */
  standDownTime: string | null | undefined,
): string => displaySlotLabel({ slotLabel: label, hasFixedTime: false, reportingTime, standDownTime });
import { BASICS_COPY, COMMAND_COPY, ENDS_COPY, PLACES_COPY, TIMING_COPY } from './copy';
import { DEFAULT_NIGHT_SHIFT, DEFAULT_SHIFT, type ArrangementDraft, type ShiftRow } from './draft';
import { PlaceAutocomplete } from './PlaceAutocomplete';

type SectionProps = {
  draft: ArrangementDraft;
  readOnly: boolean;
};

type Prefixed = { namePath?: readonly (string | number)[] };

const pathIn =
  (prefix: readonly (string | number)[] = []) =>
  (...rest: (string | number)[]): (string | number)[] => [...prefix, ...rest];

export const BasicsSection = ({
  draft,
  readOnly,
  identityLocked = false,
  derivedTitle,
  associatedUnits = [],
  confirmUnassociate = false,
}: {
  draft: { title: string };
  readOnly: boolean;
  /**
   * ⚠️ [C3c §9a] THE ORDER HAS STARTED, SO ITS IDENTITY IS FIXED — and it locks the identity
   * ONLY: the order date, the PA number, the title and the crowd estimate.
   *
   * [C2b §4]'s `corrigendum_seq` argument applied to the header: an order that has begun has
   * been CIRCULATED ON PAPER, and its number and date are what somebody rings up about. Changing
   * them once people are working to it makes the screen disagree with the sheet in their hand.
   *
   * ⚠️ THE DESCRIPTION, THE ASSOCIATED UNITS AND THE ATTACHMENTS STAY OPEN, DELIBERATELY. §9a
   * read literally would lock all of step 1, and two of those would be wrong: [C1b §4d] records
   * that withdrawing sight from a unit that no longer needs it is an ordinary act on a LIVE
   * order, and [C3a §3] put the file control on this step precisely because the signed order
   * arrives AFTER the duty starts.
   *
   * ⚠️ AND IT IS AN OFFER GATE, NOT A CONTROL. `updateArrangement` refuses none of this — the
   * predicate behind it reads the BROWSER's clock, which everything else in this repository
   * refuses to trust. `accessRole.ts` states the same thing about every predicate in it: the API
   * is the gate. Enforcing it server-side is its own pass, and it needs a decision about whether
   * `paDate` — itself one of the locked fields — may move a start date.
   *
   * ⚠️ DEFAULTED, so `StandingOrderBuilder`'s use of this section is byte-identical.
   */
  identityLocked?: boolean;
  derivedTitle: string | null;
  associatedUnits?: readonly AssociatedUnitDto[];
  confirmUnassociate?: boolean;
}) => (
  <div className="flex flex-col gap-4">
    {/* ⚠️ [C3c §9a] The reason on screen, and it names what is STILL editable — a sentence saying
        only "this cannot be changed" would be false of two thirds of this section. */}
    {identityLocked && (
      <Alert type="info" showIcon data-testid="identity-locked" message={BASICS_COPY.identityLocked} />
    )}
    <div className="grid gap-4 sm:grid-cols-2">
      <Form.Item name="paDate" label="Order date" tooltip={BASICS_COPY.paDate}>
        <DatePicker className="w-full" disabled={readOnly || identityLocked} />
      </Form.Item>
      {/*
        ⚠️ [amended C3c.2] THE PA ORDER NUMBER IS EDITABLE ON A STARTED ORDER — a client decision,
        and a REVERSAL of §9a's own argument.

        That lock applied [C2b §4]'s "a number circulated on paper cannot be renumbered" to the
        header. It reads the case backwards: the paper usually arrives AFTER the order is raised,
        so the number is most often typed in once the duty is already under way — and freezing it
        left the one field that identifies the order to the outside world permanently blank.
      */}
      <Form.Item name="paNumber" label="PA Order #" tooltip={BASICS_COPY.paNumber}>
        <Input disabled={readOnly} maxLength={20} />
      </Form.Item>
    </div>

    {/* ⚠️ [amended C3c.2] AND THE TITLE, for the same client decision — a wording correction on a
        live order is an ordinary act, and the derived fallback is what most orders show anyway. */}
    <Form.Item name="title" label="Title" tooltip={BASICS_COPY.titleDerived}>
      <Input
        disabled={readOnly}
        maxLength={200}
        placeholder={derivedTitle ?? undefined}
        data-testid="arrangement-title"
      />
    </Form.Item>

    <Form.Item name="description" label="Description">
      <Input.TextArea disabled={readOnly} rows={2} maxLength={2_000} />
    </Form.Item>

    <Form.Item name="estimatedCrowdSize" label="Estimated crowd size">
      <Input disabled={readOnly || identityLocked} maxLength={60} placeholder="about 20,000" data-testid="crowd-size" />
    </Form.Item>

    <Form.Item
      name="associatedOrgUnitIds"
      label="Associated/Related Police Stations"
      tooltip={BASICS_COPY.associatedUnits}
      extra={BASICS_COPY.associatedUnitsHint}
    >
      <AssociatedUnitsPicker disabled={readOnly} chosen={associatedUnits} confirmRemoval={confirmUnassociate} />
    </Form.Item>

    <span className="sr-only" data-testid="derived-title">
      {derivedTitle ?? ''}
    </span>
    <span className="hidden">{draft.title}</span>
  </div>
);

export const PlacesSection = ({
  draft,
  readOnly,
  namePath,
  onAddPlace,
  label,
}: SectionProps & Prefixed & { onAddPlace?: () => void; label?: string }) => (
  <div className="flex flex-col gap-2">
    {(label ?? onAddPlace) && (
      <div className="flex min-h-6 items-center justify-between gap-2">
        {label ? <span className="text-sm">{label}</span> : <span />}
        {onAddPlace && (
          <Button
            type="link"
            size="small"
            htmlType="button"
            disabled={readOnly}
            className="h-auto p-0"
            data-testid="add-place"
            onClick={onAddPlace}
          >
            {PLACES_COPY.addPlace}
          </Button>
        )}
      </div>
    )}

    <Form.List name={pathIn(namePath)(namePath ? 'places' : 'locations')}>
      {(fields, { remove }) => (
        <>
          {fields.length === 0 && (
            <EmptyState
              size="sm"
              title={PLACES_COPY.noPlacesYet}
              action={
                onAddPlace && (
                  <Button
                    size="small"
                    htmlType="button"
                    disabled={readOnly}
                    data-testid="add-first-place"
                    onClick={onAddPlace}
                  >
                    {PLACES_COPY.addFirstPlace}
                  </Button>
                )
              }
            />
          )}
          {fields.map((field, index) => (
            <div key={field.key} className="flex items-start gap-2">
              <Form.Item {...field} className="mb-0 flex-1" rules={[{ required: true, message: 'Name the place' }]}>
                <PlaceAutocomplete disabled={readOnly} data-testid={`location-${String(index)}`} />
              </Form.Item>
              <Button
                type="text"
                disabled={readOnly || fields.length === 1}
                aria-label={`Remove place ${String(index + 1)}`}
                icon={<Trash2 className="size-4" />}
                onClick={() => remove(field.name)}
              />
            </div>
          ))}
        </>
      )}
    </Form.List>

    <span className="hidden">{draft.placeCount}</span>
  </div>
);

const spansMidnight = (start: string, end: string): boolean => Boolean(start) && Boolean(end) && end <= start;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [STAND-DOWN] THE LABELS ARE COSMETIC; THE VALUES ARE UNCHANGED.
 *
 *  A whole-day combination (`…__NO_FIXED_TIME`) now reads `… · Single Shift`, because with a
 *  reporting time and an optional stand-down time it IS the duty's one shift. `Date` became
 *  `Single Date` and `Repeat` became `Multiple Dates`. Nothing stored or sent moves.
 *
 *  ⚠️ THE TWO TIMED SINGLE-SHIFT COMBINATIONS ARE HIDDEN, NOT DELETED — `SURFACES.
 *  singleShiftArrangementTypes`. Their labels carry `(fixed times)` so that, when a stored order
 *  of that type appends its own option, it cannot be confused with the whole-day one above it.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const ARRANGEMENT_TYPES = [
  { value: 'ONE_DAY__NO_FIXED_TIME', label: 'Single Date · Single Shift', timedSingleShift: false },
  { value: 'ONE_DAY__SINGLE', label: 'Single Date · Single Shift (fixed times)', timedSingleShift: true },
  { value: 'ONE_DAY__MULTIPLE', label: 'Single Date · Multiple Shifts', timedSingleShift: false },
  { value: 'WEEKDAYS__NO_FIXED_TIME', label: 'Multiple Dates · Single Shift', timedSingleShift: false },
  { value: 'WEEKDAYS__SINGLE', label: 'Multiple Dates · Single Shift (fixed times)', timedSingleShift: true },
  { value: 'WEEKDAYS__MULTIPLE', label: 'Multiple Dates · Multiple Shifts', timedSingleShift: false },
] as const;

/**
 * What the select offers, plus the stored value's own option when it is not offered.
 *
 * ⚠️ APPENDED, NEVER ADMITTED — `lib/selectOptions.ts`' rule. A stored range or a hidden timed
 * single shift renders its real name, and is still not offered to anybody choosing afresh.
 */
const arrangementTypeOptions = (
  comboValue: string,
  storedRange: { value: string; label: string } | null,
): { value: string; label: string }[] => {
  const offered = ARRANGEMENT_TYPES.filter(
    (type) => SURFACES.singleShiftArrangementTypes || !type.timedSingleShift,
  ).map(({ value, label }) => ({ value, label }));
  if (storedRange !== null) return [...offered, storedRange];
  if (offered.some((option) => option.value === comboValue)) return offered;
  const stored = ARRANGEMENT_TYPES.find((type) => type.value === comboValue);
  return stored ? [...offered, { value: stored.value, label: stored.label }] : offered;
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C2b] A STORED `DATE_RANGE` IS SHOWN TRUTHFULLY AND REFUSES TO BE RE-TYPED.
 *
 *  `4099115` replaced two `Segmented` strips with the six combinations above and left
 *  `DATE_RANGE` out of them, while `arrangement.schema.ts` and `formValues.ts` all still
 *  support it. [C2a] flagged the consequence under its own heading and did not fix it:
 *  `comboValue` collapsed anything non-`WEEKDAYS` to `ONE_DAY`, so a saved 15–17 August order
 *  DISPLAYED as "Date · Whole Day" — and one touch of the select wrote that collapse into the
 *  record, turning three days into one with nothing on screen to say so.
 *
 *  ⚠️ THE FIX IS DISPLAY PLUS REFUSAL, NOT A SEVENTH OPTION. Restoring `Range · …` combos
 *  would reverse a UI decision that was made deliberately, on the screen this pass is already
 *  reorganising. Retiring `DATE_RANGE` from the schema is a migration. Showing the truth and
 *  declining to overwrite it costs neither, and it stops the data loss today.
 *
 *  ⚠️ THE DATES THEMSELVES STAY EDITABLE. `draft.recurrenceKind` is untouched, so the date row
 *  below still renders `From` · `Ends` · `Until` and the operator can still move them. What is
 *  refused is the one control that would DISCARD the range.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const rangeTypeOption = (timingKind: ArrangementDraft['timingKind']): { value: string; label: string } => ({
  value: `DATE_RANGE__${timingKind}`,
  label:
    /* [STAND-DOWN] The same vocabulary as `ARRANGEMENT_TYPES`, so a stored range reads consistently. */
    timingKind === 'NO_FIXED_TIME'
      ? 'Date Range · Single Shift'
      : timingKind === 'SINGLE'
        ? 'Date Range · Single Shift (fixed times)'
        : 'Date Range · Multiple Shifts',
});

export const TimingSection = ({
  draft,
  readOnly,
  slots,
  showsSlotStrip,
  namePath,
  onAddShift,
  policySlot,
  zoneSlot,
  sectorSlot,
}: SectionProps &
  Prefixed & {
    slots: readonly Slot[];
    showsSlotStrip: boolean;
    onAddShift?: () => void;
    /**
     * ⚠️ [C2b §1a] THE POLICY CONTROL, RENDERED BESIDE `Arrangement type`.
     *
     * The policy decides the force grid's COLUMNS, so it belongs above that grid rather than
     * below the schedule — reading order is place → type and policy → dates → force.
     *
     * ⚠️ IT IS A SLOT RATHER THAN A MOVE, because the control needs eight of the card's props
     * (the policy list, the archived-policy resolver, the version label, the fork notice, the
     * version-moved offer) and this section has none of them. Handing the JSX in keeps
     * `TimingSection` about time.
     *
     * ⚠️ AND IT IS OPTIONAL, so a caller with no policy renders the row alone.
     */
    policySlot?: ReactNode;
    /**
     * ⚠️ [C4a §4c] THE ZONE CONTROL, BETWEEN `Arrangement type` AND THE POLICY — a slot for the
     * policy's reason: its options are the OTHER cards' zones, which this section cannot see.
     * Optional, so `StandingOrderBuilder` (no duty points) renders the row as it always did.
     */
    zoneSlot?: ReactNode;
    /**
     * [C5c §4] THE SECTOR CONTROL, RIGHT AFTER THE ZONE — a slot for the zone's reason (its options are
     * the other cards' sectors). Optional, so `StandingOrderBuilder` renders the row as it always did.
     */
    sectorSlot?: ReactNode;
  }) => {
  const form = Form.useFormInstance();
  const at = pathIn(namePath);
  /*
   * ⚠️ [C2b §1b] ONE CARD'S SUFFIX, so a stack of duty points does not render several nodes
   * under one test id. `DutyPointCard` passes `['dutyPoints', index]`, which is where the
   * number comes from; a caller with no prefix (`StandingOrderBuilder`) gets the bare id.
   */
  const suffix = typeof namePath?.[1] === 'number' ? `-${String(namePath[1])}` : '';

  /*
   * ⚠️ [C2b] A STORED RANGE NAMES ITSELF rather than collapsing to `ONE_DAY` — see
   * `rangeTypeOption`. `isStoredRange` is what makes the control read-only below.
   */
  const isStoredRange = draft.recurrenceKind === 'DATE_RANGE';
  const comboValue = `${isStoredRange ? 'DATE_RANGE' : draft.recurrenceKind === 'WEEKDAYS' ? 'WEEKDAYS' : 'ONE_DAY'}__${draft.timingKind}`;

  const onTimingChange = (value: string | number): void => {
    const kind = value as ArrangementDraft['timingKind'];
    const current = (form.getFieldValue(at('shifts')) ?? []) as ShiftRow[];

    if (kind === 'SINGLE') {
      const kept = current[0] ?? DEFAULT_SHIFT();
      form.setFieldValue(at('shifts'), [kept]);
      if (current.length > 1) {
        notifyDevChange({
          where: 'Times',
          what: `${String(current.length - 1)} shift row(s) removed`,
          why: 'A single-shift order stores exactly one shift, so what is on screen has to be what will be saved — the rows used to stay visible while the record dropped them.',
        });
      }

      const liveSlotKeys = [...new Set(slots.map((slot) => slot.dutyDate))].map((date) => `${date}#${kept.id}`);
      /*
       * ⚠️ [C2b §8c] `at('commandEntries')`, NOT THE BARE ROOT PATH — A D4 LEFTOVER, FIXED.
       *
       * Command entries were a PA-level field until [D4] moved them onto the duty point. Every
       * other write in this file was prefixed; this one was not, so it wrote a root key no card
       * reads and `body()` never enumerates. The per-slot prune it performs — dropping overrides
       * that address slot keys the new shift set has destroyed — therefore never happened, and
       * a stray root `commandEntries` accumulated in the form store instead.
       *
       * ⚠️ THIS IS NOT THE ABSOLUTE PATH `Feedback.test.tsx` CALLS DELIBERATE. That one is the
       * `getFieldValue('paDate')` READ below, which reaches out of the card on purpose because
       * a card's schedule defaults to the ORDER date. A read of the order's own field is not a
       * write to a field that has moved.
       */
      form.setFieldValue(
        at('commandEntries'),
        draft.commandEntries.map((entry) => ({
          ...entry,
          perSlot: pruneHiddenState(entry.perSlot ?? [], liveSlotKeys),
        })),
      );
      return;
    }

    if (kind === 'MULTIPLE' && current.length < 2) {
      form.setFieldValue(at('shifts'), [current[0] ?? DEFAULT_SHIFT(), DEFAULT_NIGHT_SHIFT()]);
      notifyDevChange({
        where: 'Times',
        what: 'overnight shift row added',
        why: '"Multiple shifts" with one row says nothing more than "single shift" did, so it seeds the relief that makes the choice mean something.',
      });
    }
  };

  const onRecurrenceChange = (value: string | number): void => {
    if (value !== 'WEEKDAYS') return;
    const chosen = (form.getFieldValue(at('weekdays')) ?? []) as number[];
    if (chosen.length > 0) return;
    const anchor = (form.getFieldValue(at('startDate')) ?? form.getFieldValue('paDate')) as DateTime | undefined;
    /* [P7C §6a] With no anchor, the IST day's weekday — never the browser's. */
    const weekday = anchor?.isValid ? anchor.weekday : istNow().weekday;
    form.setFieldValue(at('weekdays'), [weekday]);
    notifyDevChange({
      where: 'Dates',
      what: `weekday set to ${WEEKDAY_LABELS[weekday as Weekday] ?? String(weekday)}`,
      why: "A repeating order must name at least one weekday or the schema refuses it, so switching to Repeats seeds the start date's own day rather than saving an order that cannot validate.",
    });
  };

  const onEndsChange = (value: string | number): void => {
    if (value === 'ON_DATE') {
      if (!form.getFieldValue(at('endDate'))) {
        const last = slots.at(-1)?.dutyDate;
        if (last) {
          form.setFieldValue(at('endDate'), DateTime.fromISO(last));
          notifyDevChange({
            where: 'Dates',
            what: `end date set to ${last}`,
            why: 'Switching back to "ends on a date" with no date set would collapse the order to a single day, so it seeds the last duty date the count had produced.',
          });
        }
      }
      return;
    }
    if (!form.getFieldValue(at('occurrences'))) {
      const count = Math.max(1, new Set(slots.map((slot) => slot.dutyDate)).size);
      form.setFieldValue(at('occurrences'), count);
      notifyDevChange({
        where: 'Dates',
        what: `repeat count set to ${String(count)}`,
        why: 'An empty repeat count would make the order zero days long the moment it saved, so it seeds the number of duty days the date range already implied.',
      });
    }
  };

  const onArrangementTypeChange = (combo: string): void => {
    const [rk, tk] = combo.split('__');
    if (!rk || !tk) return;
    form.setFieldValue(at('timingKind'), tk);
    form.setFieldValue(at('recurrenceKind'), rk);
    onTimingChange(tk);
    onRecurrenceChange(rk);
  };

  const lastDutyDate = slots.length > 0 ? (slots.at(-1)?.dutyDate ?? null) : null;

  /*
   * [STAND-DOWN] THE END TIME IS OPT-IN. It renders once the operator asks for it, or whenever the
   * card already holds one — so a stored stand-down time is never hidden behind the link.
   *
   * ⚠️ UNMOUNTING THE FIELD DOES NOT LOSE THE VALUE. `DutyPointStack` registers `dutyPoints`
   * wholesale ([D4]'s hidden sibling), so the key round-trips whether or not this item is mounted.
   * Clearing the field is how it is removed: `TimeField` commits `''` on blur, which fires
   * `onValuesChange` (so autosave arms) and `standDownTimeOf` sends `null`.
   */
  const [standDownOpen, setStandDownOpen] = useState(false);
  const showsStandDown = standDownOpen || (draft.standDownTime ?? '').trim().length > 0;

  return (
    /*
     * ⚠️ [amended C2a §4a] NO BOX. `4099115` wrapped this block in
     * `rounded-lg border border-border bg-muted/40 p-4`, which is a FOUR-SIDED BOX INSIDE the
     * duty point card — exactly what [D4.2] EXIT CRITERION 3 forbids, and it is what that test
     * had been failing on ever since. The rule is not "fewer boxes", it is EXACTLY ONE: the
     * card has the border, and grouping inside it is spacing and hairlines.
     *
     * ⚠️ AND THE WASH WOULD HAVE BEEN INVISIBLE ANYWAY. [D4.4 §3] MEASURED that `muted`,
     * `accent` and `secondary` all resolve to the same `hover` value — so `bg-muted/40` inside
     * a `bg-card` block is a tint of the wash the card's own neighbours already use, which is
     * the "two bands that read as different and render identically" trap that note records.
     */
    <div className="flex flex-col gap-4">
      <Form.Item name={at('timingKind')} hidden>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name={at('recurrenceKind')} hidden>
        <Input type="hidden" />
      </Form.Item>

      {/*
        ⚠️ [C2b §1a] TYPE AND POLICY ON ONE LINE. `flex-wrap` with `items-end`, so at a narrow
        card the policy drops beneath rather than squeezing the type select — the same idiom
        the date row below already uses, and NO new container-query threshold (an arbitrary
        `@min-` value would have to be a bare pixel length even inside this comment,
        `containerQueryCandidates.test.ts`).
      */}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <Form.Item
          label="Arrangement type"
          /*
           * ⚠️ `min-w-56`, NEVER `w-full`. In a flex row `w-full` makes the item 100% of the
           * CONTAINER, so the policy control beside it wrapped to a second line and §1a's whole
           * point — type and policy on one line, above the grid they decide — was lost while
           * every duty point card grew a row.
           *
           * ⚠️ AND NO TEST SEES THIS. `intakeScroll.spec.ts`'s PRECONDITION would be the one, but
           * its three overflow claims are RED AT HEAD and were before this pass — measured by
           * running that project against a HEAD build. It is a browser-only fact about a flex
           * line, so `pnpm e2e` is where it would be caught if that spec were green.
           */
          className="mb-0 min-w-56 max-w-xs"
          /* ⚠️ [C2b] The refusal SAYS WHY, where the operator meets it. */
          extra={isStoredRange ? TIMING_COPY.storedRange : undefined}
        >
          <Select
            disabled={readOnly || isStoredRange}
            value={comboValue}
            onChange={onArrangementTypeChange}
            /* ⚠️ [C2b] The stored range is appended as its OWN option rather than admitted to
               the offered set — `lib/selectOptions.ts`' rule, so it renders its real name and
               is still not offered to anybody choosing afresh. */
            options={arrangementTypeOptions(comboValue, isStoredRange ? rangeTypeOption(draft.timingKind) : null)}
            data-testid="arrangement-type"
          />
        </Form.Item>
        {/*
          ⚠️ [C4a §4c] [amended C5c §4] FOUR CONTROLS IN THIS ROW NOW — type, zone, sector, policy —
          and still `flex-wrap`, [C2b §1a]'s recorded idiom: a narrow card drops the next control
          beneath rather than squeezing the one before it, and no new container-query threshold is
          invented for a row that already wraps one control at a time.

          ⚠️ AND THE POLICY TAKES ITS OWN LINE AT EVERY WIDTH — MEASURED, AND IT REVERSES [C2b §1a]'s
          "type and policy on one line". With the sector beside the zone the four no longer fit at the
          1366 floor (the browser run put the policy on a second line there), and whether they fit at
          1440 depends on the policy's NAME, because its select has no maximum width. A row whose line
          breaks move with the data is a row nobody can describe, so the policy block is `basis-full`
          and always starts a line of its own: one fixed wrap, the same at every rung, asserted exactly
          in `e2e/specs/intakeScroll.spec.ts`. Reading order is unchanged (it is still the last child).
        */}
        {zoneSlot}
        {sectorSlot}
        {policySlot === undefined ? null : <div className="basis-full">{policySlot}</div>}
      </div>

      <div className="flex flex-wrap items-end gap-3" data-testid="date-detail">
        {draft.recurrenceKind === 'WEEKDAYS' && (
          <Form.Item name={at('weekdays')} className="mb-0" tooltip={TIMING_COPY.weekdays}>
            <Checkbox.Group disabled={readOnly} data-testid="weekday-chips">
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map((day: Weekday) => (
                  <Checkbox key={day} value={day}>
                    {WEEKDAY_LABELS[day]}
                  </Checkbox>
                ))}
              </div>
            </Checkbox.Group>
          </Form.Item>
        )}

        <Form.Item name={at('startDate')} label={draft.recurrenceKind === 'ONE_DAY' ? 'On' : 'From'} className="mb-0">
          <DatePicker disabled={readOnly} format="dd LLL yyyy" />
        </Form.Item>

        {/*
          ─────────────────────────────────────────────────────────────────────────────────
           ⚠️ [C2b §1b/§1c] REPORTING TIME SITS ON THE DATE ROW, AND ONLY ON THE WHOLE-DAY
           COMBINATIONS.

           `Date · Whole Day` puts it beside `On`; `Repeat · Whole Day` beside `From`. On the
           four shift-bearing combinations it is NOT SHOWN, because it is no longer entered.
           ⚠️ [amended C5b §3e] Nor derived: a shift card sends `null` (`reportingTimeOf`) and each
           of its duties reports at its OWN shift start — [C2b §1c]'s card-level minimum is gone.

           ⚠️ THE TWO HALVES OF "WHERE AND WHEN TO REPORT" NOW SIT ON DIFFERENT ROWS, WHICH IS
           DELIBERATE (§1e). The reporting PLACE stays with the duty place, because it is a
           place; the TIME belongs with the dates, because that is what it qualifies.

           ⚠️ NO NEW BREAKPOINT. `date-detail` is already `flex flex-wrap items-end gap-3`, so
           this is one more flex child and the row wraps as it always did.
          ─────────────────────────────────────────────────────────────────────────────────
        */}
        {draft.timingKind === 'NO_FIXED_TIME' && (
          <Form.Item name={at('reportingTime')} label={TIMING_COPY.reportingTime} className="mb-0">
            <TimeField disabled={readOnly} data-testid={`reporting-time${suffix}`} />
          </Form.Item>
        )}

        {/* [STAND-DOWN] Beside the reporting time, on the whole-day combinations only — see `showsStandDown`. */}
        {draft.timingKind === 'NO_FIXED_TIME' &&
          (showsStandDown ? (
            <Form.Item name={at('standDownTime')} label={TIMING_COPY.standDownTime} className="mb-0">
              <TimeField
                disabled={readOnly}
                aria-label={TIMING_COPY.standDownTime}
                data-testid={`stand-down-time${suffix}`}
              />
            </Form.Item>
          ) : (
            <Button
              type="link"
              htmlType="button"
              className="mb-1 px-0"
              disabled={readOnly}
              icon={<Plus className="size-3.5" />}
              data-testid={`add-stand-down-time${suffix}`}
              onClick={() => {
                setStandDownOpen(true);
              }}
            >
              {TIMING_COPY.addStandDownTime}
            </Button>
          ))}

        {draft.recurrenceKind !== 'ONE_DAY' && (
          <>
            <Form.Item name={at('endsKind')} label={ENDS_COPY.label} className="mb-0">
              <Segmented
                disabled={readOnly}
                onChange={onEndsChange}
                options={[
                  { value: 'ON_DATE', label: <Tooltip title={ENDS_COPY.onDateHint}>{ENDS_COPY.onDate}</Tooltip> },
                  { value: 'AFTER_N', label: <Tooltip title={ENDS_COPY.afterNHint}>{ENDS_COPY.afterN}</Tooltip> },
                ]}
                data-testid="ends-kind"
              />
            </Form.Item>

            <Form.Item
              name={at('endDate')}
              label="Until"
              className={cn('mb-0', draft.endsKind !== 'ON_DATE' && 'hidden')}
            >
              <DatePicker disabled={readOnly} format="dd LLL yyyy" />
            </Form.Item>

            <Form.Item
              name={at('occurrences')}
              label="Repeat"
              className={cn('mb-0', draft.endsKind !== 'AFTER_N' && 'hidden')}
            >
              <InputNumber
                disabled={readOnly}
                min={1}
                max={MAX_DUTY_DAYS}
                className="w-28"
                addonAfter={ENDS_COPY.occurrencesLabel}
                aria-label="Number of repeats"
              />
            </Form.Item>

            {draft.endsKind === 'AFTER_N' && lastDutyDate !== null && (
              <p className="mb-1 text-xs text-muted-foreground" data-testid="ends-resolved">
                {ENDS_COPY.lastDuty} {lastDutyDate}
              </p>
            )}
          </>
        )}
      </div>

      {draft.timingKind !== 'NO_FIXED_TIME' && (
        <Form.List name={at('shifts')}>
          {(fields, { remove }) => (
            <div className="flex flex-col gap-2" data-testid="shift-rows">
              {fields.map((field, index) => {
                const shift = draft.shifts[index];
                const overnight = spansMidnight(shift?.startTime ?? '', shift?.endTime ?? '');
                return (
                  <div key={field.key} className="flex flex-wrap items-start gap-2">
                    <Form.Item name={[field.name, 'name']} className="mb-0">
                      <Input
                        disabled={readOnly}
                        placeholder="Shift name"
                        className="w-32"
                        aria-label={`Shift ${String(index + 1)} name`}
                      />
                    </Form.Item>
                    <Form.Item name={[field.name, 'startTime']} className="mb-0">
                      <TimeField
                        disabled={readOnly}
                        className="w-28"
                        placeholder="From"
                        aria-label={`Shift ${String(index + 1)} start`}
                      />
                    </Form.Item>
                    <Form.Item name={[field.name, 'endTime']} className="mb-0">
                      <TimeField
                        disabled={readOnly}
                        className="w-28"
                        placeholder="To"
                        aria-label={`Shift ${String(index + 1)} end`}
                      />
                    </Form.Item>

                    {overnight && (
                      <Tooltip title={TIMING_COPY.overnightExplained}>
                        <Tag className="mb-1" data-testid={`overnight-${String(index)}`}>
                          +1 day
                        </Tag>
                      </Tooltip>
                    )}

                    {draft.timingKind === 'MULTIPLE' && (
                      <Button
                        type="text"
                        className="mb-1"
                        disabled={readOnly || fields.length === 1}
                        aria-label={`Remove shift ${String(index + 1)}`}
                        icon={<Trash2 className="size-4" />}
                        onClick={() => remove(field.name)}
                      />
                    )}
                  </div>
                );
              })}
              {overnightHint(draft)}
            </div>
          )}
        </Form.List>
      )}

      {onAddShift && draft.timingKind === 'MULTIPLE' && (
        <Button
          size="small"
          htmlType="button"
          disabled={readOnly}
          icon={<Plus className="size-3.5" />}
          data-testid="add-shift"
          onClick={onAddShift}
        >
          {TIMING_COPY.addShift}
        </Button>
      )}

      {showsSlotStrip && (
        <div className="border-t border-border pt-3" data-testid="slot-strip">
          <p className="mb-2 text-xs text-muted-foreground">
            {TIMING_COPY.slotStrip} <span className="tabular-nums">{slots.length}</span> in total.
          </p>
          <div className="flex flex-wrap gap-1.5 max-h-[150px] overflow-y-auto">
            {/*
              ⚠️ [C3a §6] THE STRIP READS A **STORED** LABEL, SO IT NEEDS THE SUBSTITUTION TOO —
              AND WITHOUT IT ONE CARD CONTRADICTED ITSELF ON ONE SCREEN. `generateSlots`' whole-day
              branch stamps `<date> · No fixed time`, while the card summary's `timePhrase`
              was converted in this same pass and reads `From 3:00 PM` two blocks above. A
              repeating whole-day order (`WEEKDAYS__NO_FIXED_TIME`, a first-class combo) is
              multi-slot, so the strip is shown and the disagreement is the ordinary case.
              ⚠️ [amended C5 §4a] The ordering trap lives in `displaySlotLabel` now — the noon meridiem
              doubles otherwise, which `timeFormat.util.ts` measured.
            */}
            {slots.map((slot) => (
              <Tag key={slot.slotKey} className="m-0">
                {slotLabelWithTimes(slot.label, draft.reportingTime, draft.standDownTime)}
              </Tag>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const overnightHint = (draft: ArrangementDraft) =>
  draft.shifts.some((shift) => spansMidnight(shift.startTime, shift.endTime)) ? (
    <p className="text-xs text-muted-foreground" data-testid="overnight-explained">
      {TIMING_COPY.overnightExplained}
    </p>
  ) : null;

/*
 * ⚠️ [C6a §4] THE COMMAND OFFICER PICKER MOVED TO `components/OfficerPicker.tsx`, UNCHANGED FOR THIS
 * HOST (`mode: 'multiple'` — the same query, the same key, the same remembered labels). The fill
 * screen's command seat is its second host, and the offered-equals-accepted rule [C2b §5] is stated
 * there once rather than twice.
 */

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C6a §2] "BY NAME" OR "ASK A DIVISION" — TWO PLAIN BUTTONS, AND THE SHAPE IS MEASURED.
 *
 *  ⚠️ NEVER AN ANTD `Segmented` OR `Button` HERE. This renders once per role per card, and every
 *  card body is always mounted; [C5c §2] measured ONE antd `Button` per force column taking a builder
 *  test from about 20s to about 148s, and this would be a pair per role. `ForceGrid`'s "Link all"
 *  toggle is the precedent — a plain `<button aria-pressed>` with the same classes.
 *
 *  ⚠️ AND NO TOOLTIP. `e2e/fixtures/overlays.ts` pins this file's overlay counts, and a sentence the
 *  operator needs is said in the editor itself (`askHint`), where it is read, not on a hover.
 *
 *  ⚠️ AND NO FOUR-SIDED BORDER. [D4.2]'s rule is exactly one box per duty point — the card — and
 *  `ArrangementBuilder.test.tsx` EXIT CRITERION 3 refuses any node inside it carrying `border`. The
 *  pressed one takes the `active` wash, as the force grid's "Link all" does — never a shadow, because
 *  elevation here is lightness (`DESIGN_LANGUAGE.md`).
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const MODE_BUTTON =
  'inline-flex h-6 items-center whitespace-nowrap rounded px-2 text-xs transition-colors hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const AskModeSwitch = ({
  label,
  rowKey,
  askMode,
  onByName,
  onAsk,
}: {
  /** The group's accessible name, carrying the card's place — a role repeats on every card. */
  label: string;
  rowKey: string;
  askMode: boolean;
  onByName: () => void;
  onAsk: () => void;
}) => (
  <span
    role="group"
    aria-label={label}
    className="inline-flex items-center gap-0.5"
    data-testid={`command-mode-${rowKey}`}
  >
    <button
      type="button"
      aria-pressed={!askMode}
      data-testid={`command-mode-name-${rowKey}`}
      className={cn(MODE_BUTTON, !askMode ? 'bg-active text-foreground' : 'text-muted-foreground')}
      onClick={onByName}
    >
      {COMMAND_COPY.byName}
    </button>
    <button
      type="button"
      aria-pressed={askMode}
      data-testid={`command-mode-ask-${rowKey}`}
      className={cn(MODE_BUTTON, askMode ? 'bg-active text-foreground' : 'text-muted-foreground')}
      onClick={onAsk}
    >
      {COMMAND_COPY.askDivision}
    </button>
  </span>
);

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C6a §2] THE ASK EDITOR — the fill screen's delegation editor, with a division + unit target.
 *
 *  ⚠️ THE SAME INTERACTION AS SPLITTING A FORCE SOURCE ([C6a §2d]): stacked rows, add-another
 *  beneath, Confirm/Cancel on the first row, left-aligned. The target is `SourceCell` — the force
 *  grid's own division-then-unit picker — so "which divisions may I ask" has one answer on the card.
 *
 *  ⚠️ CONFIRM EXPANDS, AND ONLY CONFIRM WRITES. Each row's number becomes that many positions
 *  (`expandAskRows`); typing writes nothing, so an unconfirmed row is not a save and not a 422.
 *
 *  ⚠️ NO CEILING, POOL OR FLOOR SENTENCE. Those are the force host's, and a command position is
 *  never stated as a number against a force figure (FR-5.2.6b); the bound here is the wire's own
 *  per-role limit, enforced by refusing to confirm past it.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const AskEditor = ({
  role,
  rowKey,
  context,
  asks,
  hasNamed,
  orgUnits,
  onConfirm,
  onCancel,
}: {
  role: string;
  rowKey: string;
  context?: string | undefined;
  asks: readonly CommandAskInputDto[] | undefined;
  hasNamed: boolean;
  orgUnits?: ReadonlyMap<string, { name: string; shortCode: string }> | undefined;
  onConfirm: (asks: CommandAskInputDto[]) => void;
  onCancel: () => void;
}) => {
  const limit = ARRANGEMENT_LIMITS.COMMAND_OFFICERS_PER_ROLE;
  const positionsOf = (rows: readonly DelegationRow[]): number =>
    rows.reduce((running, row) => running + (askTargetOf(row.target).divisionOrgUnitId === '' ? 0 : row.count), 0);
  const where = (label: string): string => (context ? `${label}, ${context}` : label);

  return (
    <DelegationRowsEditor
      seed={askRowsOf(asks)}
      renderTarget={({ index, value, onChange }) => {
        const target = askTargetOf(value);
        return (
          <SourceCell
            divisionOrgUnitId={target.divisionOrgUnitId}
            unitOrgUnitId={target.unitOrgUnitId}
            orgUnits={orgUnits}
            ariaContext={where(COMMAND_COPY.askTargetContext(role, index + 1))}
            /* ⚠️ A new division clears the unit in the SAME write — `ForceGrid`'s `setDivision` rule. */
            onChangeDivision={(next) => {
              onChange(next ? askTargetKey(next, null) : null);
            }}
            onChangeUnit={(next) => {
              onChange(target.divisionOrgUnitId === '' ? null : askTargetKey(target.divisionOrgUnitId, next));
            }}
          />
        );
      }}
      /* A row that names a division asks for at least one position; a blank row starts there too. */
      minFor={() => 1}
      max={limit}
      canAddRow={(rows) => {
        const last = rows[rows.length - 1];
        return (
          last !== undefined &&
          askTargetOf(last.target).divisionOrgUnitId !== '' &&
          rows.length < limit &&
          positionsOf(rows) < limit
        );
      }}
      canConfirm={(rows) =>
        rows.some((row) => askTargetOf(row.target).divisionOrgUnitId !== '') &&
        rows.every(
          (row) => askTargetOf(row.target).divisionOrgUnitId === '' || (Number.isInteger(row.count) && row.count >= 1),
        ) &&
        positionsOf(rows) <= limit
      }
      onConfirm={(rows) => {
        onConfirm(expandAskRows(rows, asks));
      }}
      onCancel={onCancel}
      testIdPrefix={`command-ask-${rowKey}`}
      rowAttribute="data-command-delegate-row"
      copy={{
        confirm: COMMAND_COPY.askConfirm,
        cancel: COMMAND_COPY.askCancel,
        addRow: COMMAND_COPY.askAddRow,
        refused: COMMAND_COPY.askRefused,
        countLabel: (_row, index) => where(COMMAND_COPY.askCount(role, index + 1)),
      }}
      footer={
        <>
          <p className="text-xs text-muted-foreground" data-testid={`command-ask-${rowKey}-hint`}>
            {COMMAND_COPY.askHint}
          </p>
          {hasNamed ? (
            <p className="text-xs text-warning" data-testid={`command-ask-${rowKey}-replaces`}>
              {COMMAND_COPY.askReplacesNamed(role)}
            </p>
          ) : null}
        </>
      }
    />
  );
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C6a §2] AN ASKED ROLE, AT REST — who it is asked of, in the codes an operator reads.
 *
 *  On a DRAFT: "Asked of CD · Any", and a line per addressee asked for more than one position — a
 *  figure only there, on its own line, never beside a force number. Change re-opens the editor
 *  seeded with the rows; Remove takes the role back to "By name".
 *
 *  ⚠️ ON AN ISSUED ORDER IT IS READ-ONLY AND SAYS WHO ANSWERED. Each position reads the officer its
 *  addressee named, or "Awaited from CD" — from the record's `asks[].seat`, never from `officerIds`,
 *  so a division's choice cannot become the creator's on the next save ([C1b §3]).
 *
 *  ⚠️ [C6a review] "AWAITED" ONLY WHILE THE ORDER IS LIVE — `awaitedLive`, `awaitedIsLive` of the
 *  record, REQUIRED like `CommandContacts`' `live`. On a CLOSED, CANCELLED or archived order nobody can
 *  name the position, and the record page and the preview say `Not named by CD`; the builder opens
 *  such an order through `/edit`, and saying "Awaited" there contradicted them about the same seat.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const AskedSummary = ({
  role,
  context,
  rowKey,
  asks,
  commandAsks,
  orgUnits,
  issued,
  awaitedLive,
  onChange,
  onRemove,
}: {
  role: string;
  context?: string | undefined;
  rowKey: string;
  asks: readonly CommandAskInputDto[];
  commandAsks?: ReadonlyMap<string, CommandAskDto> | undefined;
  orgUnits?: ReadonlyMap<string, { name: string; shortCode: string }> | undefined;
  issued: boolean;
  awaitedLive: boolean;
  onChange?: (() => void) | undefined;
  onRemove?: (() => void) | undefined;
}) => {
  /* ⚠️ The page's tree first, the record's own summaries second — a stored id outside the caller's
     scope is still named, never printed as an ObjectId (#84). */
  const recorded = new Map<string, { name: string; shortCode: string }>();
  for (const ask of commandAsks?.values() ?? []) {
    recorded.set(ask.division.id, ask.division);
    if (ask.unit) recorded.set(ask.unit.id, ask.unit);
    if (ask.seat) recorded.set(ask.seat.addressee.id, ask.seat.addressee);
  }
  const unitOf = (id: string | null): { name: string; shortCode: string } | null =>
    id === null ? null : (orgUnits?.get(id) ?? recorded.get(id) ?? null);
  const where = (label: string): string => (context ? `${label}, ${context}` : label);

  const real = asks.filter(commandAskIsReal);
  const seats = real.map((ask) => ({ ask, recorded: ask.askId ? commandAsks?.get(ask.askId) : undefined }));

  if (issued && seats.every((row) => row.recorded?.seat)) {
    return (
      <ul className="flex flex-col gap-1 text-sm" data-testid={`command-ask-seats-${rowKey}`}>
        {seats.map(({ ask, recorded: dto }) => {
          const seat = dto?.seat;
          const division = unitOf(ask.divisionOrgUnitId);
          const owes = seat?.handedDown ? seat.addressee : unitOf(ask.unitOrgUnitId ?? null);
          return (
            <li key={ask.askId ?? `${ask.divisionOrgUnitId}-${ask.unitOrgUnitId ?? ''}`}>
              {seat?.officer ? (
                <span>
                  {seat.officer.name}
                  {seat.officer.rankShortName ? (
                    <span className="text-muted-foreground"> · {seat.officer.rankShortName}</span>
                  ) : null}
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {(awaitedLive ? COMMAND_COPY.awaitedFrom : COMMAND_COPY.notNamedBy)(
                    commandAddresseeCode(division ?? { shortCode: '—' }, owes),
                  )}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  const addressees = askedAddressees(real);
  return (
    <div className="flex flex-col gap-1" data-testid={`command-ask-summary-${rowKey}`}>
      <p className="flex flex-wrap items-center gap-1 text-sm">
        <span className="text-muted-foreground">{COMMAND_COPY.askedOf}</span>
        {addressees.map((row) => (
          <AddresseeCodes
            key={askTargetKey(row.divisionOrgUnitId, row.unitOrgUnitId)}
            division={unitOf(row.divisionOrgUnitId)}
            unit={unitOf(row.unitOrgUnitId)}
          />
        ))}
        {onChange ? (
          <button
            type="button"
            className="ml-1 text-xs text-link hover:underline"
            aria-label={where(COMMAND_COPY.askChangeLabel(role))}
            data-testid={`command-ask-change-${rowKey}`}
            onClick={onChange}
          >
            {COMMAND_COPY.askChange}
          </button>
        ) : null}
        {onRemove ? (
          <button
            type="button"
            className="text-xs text-link hover:underline"
            aria-label={where(COMMAND_COPY.askRemoveLabel(role))}
            data-testid={`command-ask-remove-${rowKey}`}
            onClick={onRemove}
          >
            {COMMAND_COPY.askRemove}
          </button>
        ) : null}
      </p>
      {addressees
        .filter((row) => row.positions > 1)
        .map((row) => (
          <p
            key={askTargetKey(row.divisionOrgUnitId, row.unitOrgUnitId)}
            className="text-xs text-muted-foreground"
            data-testid={`command-ask-positions-${rowKey}`}
          >
            {COMMAND_COPY.askPositions(
              row.positions,
              commandAddresseeCode(unitOf(row.divisionOrgUnitId) ?? { shortCode: '—' }, unitOf(row.unitOrgUnitId)),
            )}
          </p>
        ))}
    </div>
  );
};

type CommandRow = {
  key: string;
  roleKey: string | null;
  adHocLabel: string | null;
  label: string;
  isRequired: boolean;
  eligibleRankIds: readonly string[];
  isAdHoc: boolean;
  /** An asked role the card's version no longer carries — shown so its ask can be removed. */
  isOrphan: boolean;
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C2b §8c/§8d] EVERY WRITE IN HERE MUST TELL THE PAGE, AND FOR A YEAR NONE OF THEM DID.
 *
 *  Two separate bug reports — "a custom command role is not persisted" and "autosave does not
 *  fire when command-role officers are selected" — are ONE defect with one cause.
 *
 *  rc-field-form fires `onValuesChange` from `updateValue`, the path a registered control's
 *  own change takes. It does NOT fire it from `setFieldValue`. Every writer below uses
 *  `setFieldValue`, so `dirtyRef` stayed false, `armingOf` answered `'nothing-to-save'`, and
 *  blur, step-change and unmount all returned before the PATCH. The entry sat in the form
 *  store, perfectly correct, and died at unmount.
 *
 *  ⚠️ THE ASYMMETRY IS THE PROOF, AND IT IS ONE FILE OVER. The ad-hoc force COLUMN — the twin
 *  feature — goes through `editCards`, which ends with `onValuesChange()`. This path had no
 *  such call and this component had no such prop. `ArrangementBuilder/index.tsx` states the
 *  rule out loud where the other half obeys it: *"`onValuesChange()` IS MANDATORY, AND SILENT
 *  IF FORGOTTEN."*
 *
 *  ⚠️ IT PRESENTED AS INTERMITTENT, WHICH IS WHY IT SURVIVED. `body()` reads
 *  `getFieldsValue(true)` — ALL fields, registered or not — so the moment the operator touched
 *  any registered field in the same sitting, `dirtyRef` went true and the command entries went
 *  with it. "Sometimes my Incharge saves" is the worst diagnostic shape a defect can have.
 *
 *  ⚠️ `onChanged` IS REQUIRED, NOT OPTIONAL. This component has exactly one consumer
 *  (`StandingOrderBuilder` imports only `ExtrasSection` and `PlacesSection`), so required costs
 *  nothing — and it turns [D4.3]'s "the compiler only catches the first" into "the compiler
 *  catches all five". All five writers lose work without it, not just the ad-hoc add.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const CommandSection = ({
  draft,
  readOnly,
  roles,
  rolesKnown,
  slots,
  perSlotMapperRoleKeys,
  officers,
  namePath,
  context,
  onChanged,
  asksLocked = false,
  awaitedLive,
  commandAsks,
  orgUnits,
}: SectionProps &
  Prefixed & {
    roles: readonly CommandRoleDto[];
    /**
     * ⚠️ [C6a §2] REQUIRED — WHETHER `roles` IS THE BOUND VERSION'S ANSWER OR ITS ABSENCE. While the
     * version is in flight `roles` is `[]`, and an asked role then looks exactly like one the version
     * dropped: every ask on the card would read "No longer on this card's policy" beside a Remove that
     * deletes a perfectly good ask. Required rather than defaulted, because both defaults fail
     * silently — `true` shows that false notice, `false` hides a real dropped role again.
     */
    rolesKnown: boolean;
    slots: readonly Slot[];
    perSlotMapperRoleKeys: readonly string[];
    officers: ReadonlyMap<string, { name: string }>;
    context?: string | undefined;
    /** ⚠️ REQUIRED — see this component's header. The page's own `onValuesChange`. */
    onChanged: () => void;
    /**
     * ⚠️ [C6a §2] WHO A ROLE IS ASKED OF IS SETTLED AT PUBLISH, so the "By name / Ask a division"
     * choice and the ask editor are offered on a DRAFT only. The server refuses any change to the asks
     * of an issued order with a 409 (`asksOnIssuedOrder`); this is the OFFER gate mirroring it, and it
     * is the card's `structureLocked`, which is `status !== 'DRAFT'` — [C3c.1 §9b]. A by-name role
     * stays editable exactly as before, because the server still allows that.
     */
    asksLocked?: boolean;
    /**
     * ⚠️ [C6a review] REQUIRED — `awaitedIsLive` of the saved record, `true` before there is one. An
     * issued position nobody has named reads "Awaited from CD" only while it is; after, "Not named by
     * CD" — the record page's and the preview's words. A default would put one reading in the omitted
     * position and ship it silently ([C5b §3]'s rule for a prop that decides a word).
     */
    awaitedLive: boolean;
    /**
     * ⚠️ [C6a §2] THIS CARD'S SAVED ASKED POSITIONS, BY ASK ID (an ask id is unique on its duty point
     * only, so the page hands each card its own map) — the addressees' names for a stored
     * id outside the caller's tree, and on an issued order WHO each addressee has named. Read-only:
     * a division's answer never rides back into the form ([C1b §3], `commandAskSeatSchema`).
     */
    commandAsks?: ReadonlyMap<string, CommandAskDto>;
    /** The page's resolved org units, so an addressee is named rather than printed as an id (#84). */
    orgUnits?: ReadonlyMap<string, { name: string; shortCode: string }>;
  }) => {
  const form = Form.useFormInstance();
  const { modal } = App.useApp();
  const at = pathIn(namePath);
  const [adHocOpen, setAdHocOpen] = useState(false);
  const [adHocLabel, setAdHocLabel] = useState('');
  /*
   * ⚠️ [C6a §2] "ASK A DIVISION" PRESSED, WITH NOTHING CONFIRMED YET — LOCAL STATE, AND IT WRITES
   * NOTHING. The role's mode on the RECORD is derived from its asks (`asks.some(commandAskIsReal)`);
   * this set only says which roles have the editor open. An operator who presses the button and walks
   * away has changed nothing, so autosave must not arm — the empty-toggle trap [C5c §8e] names. The
   * stated cost, the fill screen's own: rows typed and not confirmed are lost if the page is left.
   */
  const [askEditing, setAskEditing] = useState<ReadonlySet<string>>(() => new Set());
  const setEditing = (key: string, open: boolean): void => {
    setAskEditing((current) => {
      const next = new Set(current);
      if (open) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C2b §5g] THE **OFFERED** SET, EXPANDED WITH THE SAME FUNCTION THE SERVER USES.
   *
   *  A command role's allowed ranks is a MIXED list (§5c). The wire carries the RAW pair —
   *  ids and group keys — because collapsing it server-side would lose the distinction on a
   *  round trip through the policy builder. So the two readers each expand, and the whole point
   *  is that they expand with ONE function: `expandRankSelection`.
   *
   *  The other reader is `policyCommandRoster`, which feeds `assertCommandOfficersEligible` —
   *  the ACCEPTED set. A rule expressed twice drifts, and the visible cost is a sheet that
   *  OFFERS an officer and then REFUSES them, which tells the operator their own screen was
   *  wrong. `eligibleRankIdsFor` exists for exactly this and states the property.
   *
   *  ⚠️ THE MASTER IS ALREADY CACHED. `useRankOptions` is the same bounded, 30s-stale query the
   *  rank pickers use, so this costs no round trip in practice — and it is why
   *  `ranks.is_command_eligible` needs no index and no list filter.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const { ranks } = useRankOptions();
  const rankIdByCode = new Map(ranks.flatMap((rank) => (rank.code ? [[rank.code, rank.id] as const] : [])));

  const rows: CommandRow[] = [
    ...roles.map((role) => ({
      key: commandKeyOf({ roleKey: role.key }),
      roleKey: role.key,
      adHocLabel: null,
      label: role.label,
      isRequired: role.isRequired,
      eligibleRankIds: expandRankSelection(
        { rankIds: role.eligibleRankIds, groupKeys: role.eligibleRankGroupKeys },
        rankIdByCode,
      ),
      isAdHoc: false,
      isOrphan: false,
    })),
    /*
     * ─────────────────────────────────────────────────────────────────────────────────
     *  ⚠️ [C6a §2] AN ASKED ROLE THE CARD'S VERSION DOES NOT CARRY STILL GETS A ROW.
     *
     *  Three paths reach it with no act of the operator's on this role: `setPolicy` keeps the
     *  command entries while it re-points the version, an UNPINNED card follows a newly published
     *  version that dropped the role, and a server duplicate re-points every card at the current
     *  version. Every autosave then keeps sending the ask and the publish preview BLOCKS on it —
     *  "Choose the role again, or remove it" — so a row built from `roles` alone would leave a
     *  blocker naming something nobody can see or remove, and the draft unpublishable.
     *
     *  ⚠️ IT OFFERS REMOVE AND NOTHING ELSE. No switch, no Change and no officer picker: the version
     *  holds no label, no rank rule and no place for this key, so the only honest act is to take
     *  the ask back. Remove is `setAsks(row, [])` — the sixth writer, `onChanged` included. A BY-NAME
     *  orphan stays hidden, as before C6a: the server publishes it, and this pass does not widen that.
     *
     *  ⚠️ ONLY ONCE THE VERSION IS KNOWN (`rolesKnown`). `[]` while it loads is not "the version
     *  dropped every role", and a Remove offered in that window would delete a real ask.
     * ─────────────────────────────────────────────────────────────────────────────────
     */
    ...draft.commandEntries.flatMap((entry) =>
      rolesKnown &&
      typeof entry.roleKey === 'string' &&
      !roles.some((role) => role.key === entry.roleKey) &&
      (entry.asks ?? []).some(commandAskIsReal)
        ? [
            {
              key: commandKeyOf(entry),
              roleKey: entry.roleKey,
              adHocLabel: null,
              label: entry.roleKey,
              isRequired: false,
              eligibleRankIds: [] as readonly string[],
              isAdHoc: false,
              isOrphan: true,
            },
          ]
        : [],
    ),
    ...draft.commandEntries.flatMap((entry) =>
      entry.adHocLabel
        ? [
            {
              key: commandKeyOf(entry),
              roleKey: null,
              adHocLabel: entry.adHocLabel,
              label: entry.adHocLabel,
              isRequired: false,
              eligibleRankIds: [] as readonly string[],
              isAdHoc: true,
              isOrphan: false,
            },
          ]
        : [],
    ),
  ];

  const named = (label: string): string => (context ? `${label}, ${context}` : label);

  const entryIndex = (key: string): number => draft.commandEntries.findIndex((entry) => commandKeyOf(entry) === key);

  /*
   * ⚠️ [C2b §8c/§8d] THE ONE WRITER, AND BOTH HALVES ARE IN IT.
   *
   * Five call sites each doing `setFieldValue` then `onChanged()` is five places to forget the
   * second, and forgetting it is SILENT — see this component's header for what that cost. One
   * function makes "the form goes dirty" a property of writing rather than a step beside it,
   * which is [C1a]'s `rememberPick` rule applied to the same class of bug.
   *
   * ⚠️ A SIXTH WRITER ADDED LATER INHERITS IT. That is the whole reason it is a function.
   */
  const writeEntries = (entries: CommandEntryInputDto[]): void => {
    form.setFieldValue(at('commandEntries'), entries);
    onChanged();
  };

  const setOfficers = (row: CommandRow, officerIds: string[]): void => {
    const entries = [...draft.commandEntries];
    const index = entryIndex(row.key);
    const identity = { roleKey: row.roleKey, adHocLabel: row.adHocLabel };
    if (index === -1) entries.push({ ...identity, officerIds });
    else entries[index] = { ...entries[index], ...identity, officerIds };
    writeEntries(entries);
  };

  const setMode = (key: string, slotKey: string, mode: 'ALL' | 'ANY' | 'SPECIFIC'): void => {
    const entries = [...draft.commandEntries];
    const index = entryIndex(key);
    const entry = entries[index];
    if (!entry) return;
    const perSlot = (entry.perSlot ?? []).filter((row) => row.slotKey !== slotKey);
    const officerIds = mode === 'SPECIFIC' ? [...entry.officerIds] : [];
    entries[index] = { ...entry, perSlot: [...perSlot, { slotKey, mode, officerIds }] };
    writeEntries(entries);
  };

  const setSubset = (key: string, slotKey: string, officerIds: string[]): void => {
    const entries = [...draft.commandEntries];
    const index = entryIndex(key);
    const entry = entries[index];
    if (!entry) return;
    const perSlot = (entry.perSlot ?? []).map((row) => (row.slotKey === slotKey ? { ...row, officerIds } : row));
    entries[index] = { ...entry, perSlot };
    writeEntries(entries);
  };

  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C6a §2] THE SIXTH WRITER, AND IT GOES THROUGH `writeEntries` LIKE THE OTHER FIVE.
   *
   *  A role is named OR asked, never both (`commandEntryInputSchema`'s refines), so writing asks
   *  clears `officerIds` AND `perSlot` IN THE SAME CALL — two writes would leave one autosave between
   *  them holding both, which the server refuses. `asks: []` is "by name", so the same writer takes
   *  a role back.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const setAsks = (row: CommandRow, asks: CommandAskInputDto[]): void => {
    const entries = [...draft.commandEntries];
    const index = entryIndex(row.key);
    const identity = { roleKey: row.roleKey, adHocLabel: row.adHocLabel };
    if (index === -1) entries.push({ ...identity, officerIds: [], perSlot: [], asks });
    else entries[index] = { ...entries[index], ...identity, officerIds: [], perSlot: [], asks };
    writeEntries(entries);
  };

  const addAdHocRole = (label: string): void => {
    writeEntries([...draft.commandEntries, { roleKey: null, adHocLabel: label, officerIds: [] }]);
  };

  const removeAdHocRole = (row: CommandRow): void => {
    const drop = (): void => {
      writeEntries(draft.commandEntries.filter((entry) => commandKeyOf(entry) !== row.key));
    };

    const entry = draft.commandEntries[entryIndex(row.key)];
    /* ⚠️ [C6a review] An ASKED one-off role asks first too — the ask is what goes, and it went silently. */
    const askedHere = (entry?.asks ?? []).some(commandAskIsReal);
    if (entry === undefined || (entry.officerIds[0] === undefined && !askedHere)) {
      drop();
      return;
    }

    modal.confirm({
      title: COMMAND_COPY.removeAdHocTitle(row.label),
      content: askedHere ? COMMAND_COPY.removeAdHocAskedBody : COMMAND_COPY.removeAdHocBody(entry.officerIds),
      okText: 'Remove',
      okButtonProps: { danger: true },
      onOk: drop,
    });
  };

  /*
   * ⚠️ [C6a review] "BY NAME" ON A CONFIRMED ASK ASKS BEFORE IT DELETES. It wrote `asks: []` at once and
   * autosaved, from a switch that reads as a choice — the plan's "toggling writes nothing until
   * confirmed", broken on the way back. `modal` from `App.useApp()`, never the static (§8).
   */
  const dropAsks = (row: CommandRow): void => {
    modal.confirm({
      title: COMMAND_COPY.askDropTitle(row.label),
      content: COMMAND_COPY.askDropBody(row.label),
      okText: COMMAND_COPY.askDropConfirm,
      okButtonProps: { danger: true },
      onOk: () => {
        setAsks(row, []);
      },
    });
  };

  if (rows[0] === undefined && readOnly) {
    return <EmptyState size="sm" title="This policy defines no command roles." />;
  }

  return (
    <div className="flex flex-col gap-4">
      {rows.map((row, index) => {
        const entry = draft.commandEntries.find((candidate) => commandKeyOf(candidate) === row.key);
        const showsMapper = perSlotMapperRoleKeys.includes(row.key);
        /*
         * ⚠️ [C6a §2] THE MODE IS DERIVED FROM THE RECORD, NEVER STORED BESIDE IT. A role holding a
         * real ask IS asked; one with the editor open is being asked; everything else is by name —
         * so a stored order, a reload and a re-seed can never disagree with what is on screen.
         */
        const asked = (entry?.asks ?? []).some(commandAskIsReal);
        const offersAsk = !readOnly && !asksLocked;
        const editingAsk = offersAsk && !row.isOrphan && askEditing.has(row.key);
        const askMode = asked || editingAsk;
        return (
          <div key={row.key} className={cn('py-3', index > 0 && 'border-t border-border')}>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">{row.label}</span>
              {row.isRequired && (
                <Tooltip title={COMMAND_COPY.requiredRole}>
                  <Tag className="m-0">Required</Tag>
                </Tooltip>
              )}
              {row.isOrphan && (
                <span className="text-xs text-muted-foreground" data-testid={`command-role-orphan-${row.key}`}>
                  {COMMAND_COPY.notOnPolicy}
                </span>
              )}
              {offersAsk && !row.isOrphan && (
                <AskModeSwitch
                  label={named(COMMAND_COPY.modeGroup(row.label))}
                  rowKey={row.key}
                  askMode={askMode}
                  onByName={() => {
                    /* ⚠️ [C6a review] With the editor open it only CANCELS — "abandon this edit", never a delete. */
                    if (editingAsk) {
                      setEditing(row.key, false);
                      return;
                    }
                    if (asked) dropAsks(row);
                  }}
                  onAsk={() => {
                    if (!askMode) setEditing(row.key, true);
                  }}
                />
              )}
              {row.isAdHoc && (
                <>
                  <Tooltip title={COMMAND_COPY.adHocRole}>
                    <Tag className="m-0">{COMMAND_COPY.adHocTag}</Tag>
                  </Tooltip>
                  <Button
                    type="text"
                    size="small"
                    className="ml-auto"
                    /* ⚠️ [C6a §2] An ASKED one-off role is part of what was issued — removing it is an ask
                       change `asksOnIssuedOrder` 409s, so it is not offered once the asks are settled. */
                    disabled={readOnly || (asksLocked && asked)}
                    aria-label={named(COMMAND_COPY.removeAdHocLabel(row.label))}
                    data-testid={`remove-ad-hoc-role-${row.key}`}
                    onClick={() => {
                      removeAdHocRole(row);
                    }}
                  >
                    <X className="size-4" aria-hidden />
                  </Button>
                </>
              )}
            </div>
            {askMode ? (
              editingAsk ? (
                <AskEditor
                  role={row.label}
                  rowKey={row.key}
                  context={context}
                  asks={entry?.asks}
                  hasNamed={entry?.officerIds[0] !== undefined}
                  orgUnits={orgUnits}
                  onConfirm={(asks) => {
                    setAsks(row, asks);
                    setEditing(row.key, false);
                  }}
                  onCancel={() => {
                    setEditing(row.key, false);
                  }}
                />
              ) : (
                <AskedSummary
                  role={row.label}
                  context={context}
                  rowKey={row.key}
                  asks={entry?.asks ?? []}
                  commandAsks={commandAsks}
                  orgUnits={orgUnits}
                  issued={asksLocked}
                  awaitedLive={awaitedLive}
                  onChange={
                    offersAsk && !row.isOrphan
                      ? () => {
                          setEditing(row.key, true);
                        }
                      : undefined
                  }
                  onRemove={
                    offersAsk
                      ? () => {
                          setAsks(row, []);
                        }
                      : undefined
                  }
                />
              )
            ) : (
              <Tooltip title={COMMAND_COPY.officerPicker}>
                <span>
                  <OfficerPicker
                    mode="multiple"
                    label={`Officers for ${row.label}`}
                    disabled={readOnly}
                    value={entry?.officerIds ?? []}
                    resolved={officers}
                    eligibleRankIds={row.eligibleRankIds}
                    onChange={(ids) => setOfficers(row, ids)}
                  />
                </span>
              </Tooltip>
            )}

            {!askMode && showsMapper && (
              <div className="mt-3 border-t border-border pt-3" data-testid={`per-slot-mapper-${row.key}`}>
                <p className="mb-2 text-xs text-muted-foreground">{COMMAND_COPY.perShiftIntro}</p>
                <div className="flex flex-col gap-2">
                  {slots.map((slot) => {
                    const override = entry?.perSlot?.find((candidate) => candidate.slotKey === slot.slotKey);
                    const current = override?.mode ?? 'ALL';
                    return (
                      <div key={slot.slotKey} className="flex flex-col gap-1">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          {/* ⚠️ [C3a §6] The same stored label, the same substitution — see the
                              slot strip in `TimingSection`. */}
                          <span className="text-xs">
                            {slotLabelWithTimes(slot.label, draft.reportingTime, draft.standDownTime)}
                          </span>
                          <Segmented
                            size="small"
                            disabled={readOnly}
                            value={current}
                            onChange={(value) => setMode(row.key, slot.slotKey, value as 'ALL' | 'ANY' | 'SPECIFIC')}
                            options={[
                              { value: 'ALL', label: <Tooltip title={COMMAND_COPY.modeAll}>All</Tooltip> },
                              { value: 'ANY', label: <Tooltip title={COMMAND_COPY.modeAny}>Any</Tooltip> },
                              {
                                value: 'SPECIFIC',
                                label: <Tooltip title={COMMAND_COPY.modeSpecific}>Specific</Tooltip>,
                              },
                            ]}
                          />
                        </div>

                        {current === 'SPECIFIC' && (
                          <Checkbox.Group
                            className="pl-2"
                            disabled={readOnly}
                            value={override?.officerIds ?? []}
                            onChange={(values) => setSubset(row.key, slot.slotKey, values)}
                            data-testid={`subset-${row.key}-${slot.slotKey}`}
                            options={(entry?.officerIds ?? []).map((officerId) => ({
                              value: officerId,
                              label: nameOr(officers.get(officerId)),
                            }))}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {!readOnly && (
        <div>
          <Tooltip title={COMMAND_COPY.adHocRole}>
            <Button
              size="small"
              aria-label={named(COMMAND_COPY.addAdHocLabel)}
              data-testid="add-ad-hoc-role"
              onClick={() => {
                setAdHocOpen(true);
              }}
            >
              {COMMAND_COPY.addAdHoc}
            </Button>
          </Tooltip>
        </div>
      )}

      {adHocOpen && (
        <Modal
          open
          title={COMMAND_COPY.addAdHocTitle}
          okText="Add"
          okButtonProps={{ disabled: adHocLabel.trim() === '' }}
          onCancel={() => {
            setAdHocOpen(false);
            setAdHocLabel('');
          }}
          onOk={() => {
            addAdHocRole(adHocLabel.trim());
            setAdHocOpen(false);
            setAdHocLabel('');
          }}
        >
          <p className="mb-2 text-sm text-muted-foreground">{COMMAND_COPY.adHocRole}</p>
          <Input
            aria-label={COMMAND_COPY.addAdHocTitle}
            data-testid="ad-hoc-role-label"
            maxLength={80}
            value={adHocLabel}
            onChange={(event) => {
              setAdHocLabel(event.target.value);
            }}
          />
        </Modal>
      )}

      <Form.Item name={at('commandEntries')} hidden>
        <Input type="hidden" />
      </Form.Item>
    </div>
  );
};

const InstructionList = ({
  value,
  onChange,
  disabled,
}: {
  value?: { type: 'LINE'; text: string }[];
  onChange?: (next: { type: 'LINE'; text: string }[]) => void;
  disabled?: boolean;
}) => {
  const rows = value ?? [];
  const update = (index: number, text: string): void =>
    onChange?.(rows.map((row, position) => (position === index ? { ...row, text } : row)));

  const focusInstruction = (index: number): void => {
    requestAnimationFrame(() => {
      const field = document.querySelector<HTMLTextAreaElement>(`[aria-label="Instruction ${String(index + 1)}"]`);
      field?.focus();
      field?.setSelectionRange(field.value.length, field.value.length);
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <ReorderableList
        ariaLabel="Instructions"
        disabled={disabled}
        emptyText="No instructions yet."
        data-testid="instruction-list"
        items={rows.map((row, index) => ({
          id: `instruction-${String(index)}`,
          label: row.text.trim() || `Line ${String(index + 1)}`,
        }))}
        onReorder={(next) =>
          onChange?.(
            next.map((item) => {
              const index = Number(item.id.replace('instruction-', ''));
              return rows[index] ?? { type: 'LINE', text: '' };
            }),
          )
        }
        onRemove={(item) => {
          const index = Number(item.id.replace('instruction-', ''));
          onChange?.(rows.filter((_row, position) => position !== index));
        }}
        renderItem={(item) => {
          const index = Number(item.id.replace('instruction-', ''));
          return (
            <Input.TextArea
              disabled={disabled}
              aria-label={`Instruction ${String(index + 1)}`}
              autoSize={{ minRows: 1, maxRows: 8 }}
              placeholder="Type an instruction"
              value={rows[index]?.text ?? ''}
              onChange={(event) => {
                update(index, event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  const next = [...rows];
                  next.splice(index + 1, 0, { type: 'LINE', text: '' });
                  onChange?.(next);
                  focusInstruction(index + 1);
                  return;
                }
                if (event.key === 'Backspace' && (rows[index]?.text ?? '') === '' && rows.length > 1) {
                  event.preventDefault();
                  onChange?.(rows.filter((_row, position) => position !== index));
                  focusInstruction(Math.max(0, index - 1));
                }
              }}
            />
          );
        }}
      />
      <div>
        <Button
          size="small"
          disabled={disabled}
          onClick={() => {
            onChange?.([...rows, { type: 'LINE', text: '' }]);
            focusInstruction(rows.length);
          }}
        >
          Add line
        </Button>
      </div>
    </div>
  );
};

export const ExtrasSection = ({
  readOnly,
  namePath,
  context,
}: { readOnly: boolean } & Prefixed & { context?: string | undefined }) => {
  const at = pathIn(namePath);
  const suffix = namePath ? `-${namePath.join('-')}` : '';
  const eager = namePath === undefined;
  const named = (label: string): string => (context ? `${label}, ${context}` : label);
  return (
    <Collapse
      ghost
      data-testid={`extras${suffix}`}
      items={[
        {
          key: 'instructions',
          label: 'Instructions',
          forceRender: eager,
          children: (
            <Form.Item name={at('instructions')} noStyle>
              <InstructionList disabled={readOnly} />
            </Form.Item>
          ),
        },
        {
          key: 'supporting',
          label: 'Supporting arrangements',
          forceRender: eager,
          children: (
            <Form.List name={at('supportingArrangements')}>
              {(fields, { add, remove }) => (
                <div className="flex flex-col gap-2">
                  {fields.map((field, index) => (
                    <div key={field.key} className="flex items-start gap-2">
                      <Form.Item name={[field.name, 'personName']} className="mb-0 flex-1">
                        <Input
                          disabled={readOnly}
                          aria-label={named(`Person ${String(index + 1)}`)}
                          placeholder="Name"
                        />
                      </Form.Item>
                      <Button
                        type="text"
                        disabled={readOnly}
                        aria-label={named(`Remove person ${String(index + 1)}`)}
                        icon={<Trash2 className="size-4" />}
                        onClick={() => remove(field.name)}
                      />
                    </div>
                  ))}
                  <div>
                    <Button
                      size="small"
                      disabled={readOnly}
                      onClick={() => add({ personName: '', responsibilities: [] })}
                    >
                      Add person
                    </Button>
                  </div>
                </div>
              )}
            </Form.List>
          ),
        },
      ]}
      className={cn(readOnly && 'pointer-events-none opacity-60')}
    />
  );
};
